import { addMoney, assertMoney, ratioBasisPoints } from './money';
import { compareDays, localDayFromISO } from './dates';
import type { Account, Expense, Income, LedgerSnapshot, LocalDay, Money, Payment } from './model';

export type ExpenseStatus = 'pending' | 'partiallyPaid' | 'paid' | 'overdue' | 'canceled';

const active = <T extends { deletedAt?: string }>(records: T[]) => records.filter((record) => record.deletedAt === undefined);

export function paidAmount(expenseID: string, payments: Payment[]): Money {
  return active(payments)
    .filter((payment) => payment.expenseID === expenseID)
    .reduce((total, payment) => addMoney(total, payment.amount), 0n);
}

export function remainingAmount(expense: Expense, payments: Payment[]): Money {
  const remaining = expense.amount - paidAmount(expense.id, payments);
  return remaining > 0n ? assertMoney(remaining) : 0n;
}

export function expenseStatus(expense: Expense, payments: Payment[], today: LocalDay): ExpenseStatus {
  if (expense.canceledAt) return 'canceled';
  const paid = paidAmount(expense.id, payments);
  if (paid >= expense.amount) return 'paid';
  if (expense.dueOn && compareDays(expense.dueOn, today) < 0) return 'overdue';
  if (paid > 0n) return 'partiallyPaid';
  return 'pending';
}

export function accountBalance(accountID: string, snapshot: LedgerSnapshot, through?: string): Money {
  const included = (date: string) => !through || date <= through;
  let balance = 0n;
  for (const record of active(snapshot.adjustments)) if (record.accountID === accountID && included(record.occurredAt)) balance = addMoney(balance, record.amount);
  for (const record of active(snapshot.incomes)) if (record.accountID === accountID && included(record.receivedAt)) balance = addMoney(balance, record.amount);
  for (const record of active(snapshot.payments)) if (record.accountID === accountID && included(record.paidAt)) balance = addMoney(balance, -record.amount);
  for (const record of active(snapshot.transfers)) {
    if (!included(record.occurredAt)) continue;
    if (record.destinationAccountID === accountID) balance = addMoney(balance, record.amount);
    if (record.sourceAccountID === accountID) balance = addMoney(balance, -record.amount);
  }
  return balance;
}

export function totalBalance(snapshot: LedgerSnapshot, through?: string): Money {
  return active(snapshot.accounts).reduce((total, account) => addMoney(total, accountBalance(account.id, snapshot, through)), 0n);
}

export interface ReportSummary {
  income: Money;
  companyExpenses: Money;
  personalExpenses: Money;
  available: Money;
  companyRatioBasisPoints: number | undefined;
  personalRatioBasisPoints: number | undefined;
  incomeBySource: Record<string, Money>;
  expensesByCategory: Record<string, Money>;
  upcoming: Money;
  overdue: Money;
}

export type ReportActivity =
  | { kind: 'income'; date: LocalDay; income: Income }
  | { kind: 'payment'; date: LocalDay; payment: Payment; expense: Expense };

export interface MonthlyReportPoint {
  month: string;
  income: Money;
  companyExpenses: Money;
  personalExpenses: Money;
  available: Money;
}

function reportRange(start: LocalDay, end: LocalDay) {
  return (day: LocalDay) => compareDays(day, start) >= 0 && compareDays(day, end) <= 0;
}

export function reportActivity(snapshot: LedgerSnapshot, start: LocalDay, end: LocalDay, categoryID?: string): ReportActivity[] {
  const inRange = reportRange(start, end);
  const expenses = new Map(active(snapshot.expenses).map((expense) => [expense.id, expense]));
  const activities: ReportActivity[] = [];
  if (!categoryID) {
    for (const income of active(snapshot.incomes)) {
      const date = localDayFromISO(income.receivedAt);
      if (inRange(date)) activities.push({ kind: 'income', date, income });
    }
  }
  for (const payment of active(snapshot.payments)) {
    const date = localDayFromISO(payment.paidAt);
    const expense = expenses.get(payment.expenseID);
    if (expense && inRange(date) && (!categoryID || expense.categoryID === categoryID)) activities.push({ kind: 'payment', date, payment, expense });
  }
  return activities.sort((left, right) => right.date.localeCompare(left.date)
    || (right.kind === 'income' ? right.income.updatedAt : right.payment.updatedAt).localeCompare(left.kind === 'income' ? left.income.updatedAt : left.payment.updatedAt));
}

export function monthlyReportSeries(snapshot: LedgerSnapshot, start: LocalDay, end: LocalDay): MonthlyReportPoint[] {
  const values = new Map<string, MonthlyReportPoint>();
  let [year, month] = start.slice(0, 7).split('-').map(Number) as [number, number];
  const endMonth = end.slice(0, 7);
  while (`${year}-${String(month).padStart(2, '0')}` <= endMonth) {
    const key = `${year}-${String(month).padStart(2, '0')}`;
    values.set(key, { month: key, income: 0n, companyExpenses: 0n, personalExpenses: 0n, available: 0n });
    month += 1;
    if (month === 13) { year += 1; month = 1; }
  }
  for (const activity of reportActivity(snapshot, start, end)) {
    const point = values.get(activity.date.slice(0, 7));
    if (!point) continue;
    if (activity.kind === 'income') point.income = addMoney(point.income, activity.income.amount);
    else if (activity.expense.purpose === 'company') point.companyExpenses = addMoney(point.companyExpenses, activity.payment.amount);
    else point.personalExpenses = addMoney(point.personalExpenses, activity.payment.amount);
  }
  return [...values.values()].map((point) => ({ ...point, available: addMoney(point.income, -point.companyExpenses - point.personalExpenses) }));
}

export function reportSummary(snapshot: LedgerSnapshot, start: LocalDay, end: LocalDay, today: LocalDay): ReportSummary {
  const inRange = reportRange(start, end);
  const incomes = active(snapshot.incomes).filter((income) => inRange(localDayFromISO(income.receivedAt)));
  const income = incomes.reduce((sum, item) => addMoney(sum, item.amount), 0n);
  const incomeBySource: Record<string, Money> = {};
  for (const item of incomes) incomeBySource[item.sourceID] = addMoney(incomeBySource[item.sourceID] ?? 0n, item.amount);

  const expenses = new Map(active(snapshot.expenses).map((expense) => [expense.id, expense]));
  let companyExpenses = 0n;
  let personalExpenses = 0n;
  const expensesByCategory: Record<string, Money> = {};
  for (const payment of active(snapshot.payments)) {
    if (!inRange(localDayFromISO(payment.paidAt))) continue;
    const expense = expenses.get(payment.expenseID);
    if (!expense) continue;
    if (expense.purpose === 'company') companyExpenses = addMoney(companyExpenses, payment.amount);
    else personalExpenses = addMoney(personalExpenses, payment.amount);
    expensesByCategory[expense.categoryID] = addMoney(expensesByCategory[expense.categoryID] ?? 0n, payment.amount);
  }

  let upcoming = 0n;
  let overdue = 0n;
  for (const expense of expenses.values()) {
    if (expense.canceledAt || !expense.dueOn) continue;
    const remaining = remainingAmount(expense, snapshot.payments);
    if (remaining <= 0n) continue;
    if (compareDays(expense.dueOn, today) < 0) overdue = addMoney(overdue, remaining);
    else if (inRange(expense.dueOn)) upcoming = addMoney(upcoming, remaining);
  }

  return {
    income,
    companyExpenses,
    personalExpenses,
    available: addMoney(income, -companyExpenses - personalExpenses),
    companyRatioBasisPoints: ratioBasisPoints(companyExpenses, income),
    personalRatioBasisPoints: ratioBasisPoints(personalExpenses, income),
    incomeBySource,
    expensesByCategory,
    upcoming,
    overdue
  };
}

export function visibleAccounts(snapshot: LedgerSnapshot): Account[] {
  return active(snapshot.accounts).filter((account) => !account.isArchived);
}
